import React from "react";
import { Tooltip as MuiTooltip, IconButton, Box, useTheme } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';

/**
 * Props interface for Tooltip component
 */
interface TooltipProps {
  /** The tooltip text to display */
  name: string;
  /** Optional placement of the tooltip */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-end' | 'bottom-start' | 'left-end' | 'left-start' | 'right-end' | 'right-start' | 'top-end' | 'top-start';
  /** Optional custom icon size */
  iconSize?: 'small' | 'medium' | 'large';
  /** Optional custom styling for the container */
  sx?: object;
}

/**
 * Tooltip component - Material UI version of Tooltip
 * Displays an info icon that shows tooltip text on hover
 *
 * @param {TooltipProps} props Component properties
 * @return {React.ReactElement} The Tooltip component
 */
export const Tooltip: React.FC<TooltipProps> = ({ 
  name, 
  placement = 'top',
  iconSize = 'small',
  sx = {}
}) => {
  const theme = useTheme();

  const defaultStyles = {
    marginLeft: theme.spacing(1.875), // Equivalent to 15px
    display: 'inline-block',
    ...sx
  };

  return (
    <Box sx={defaultStyles}>
      <MuiTooltip 
        title={name}
        placement={placement}
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.common.white,
              color: theme.palette.text.primary,
              fontSize: theme.typography.caption.fontSize,
              fontFamily: theme.typography.fontFamily,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.shape.borderRadius,
              padding: theme.spacing(0.625), // Equivalent to 5px
              maxWidth: 250,
              textAlign: 'center',
              boxShadow: '0 6.7px 15px 0 rgba(0, 0, 0, 0.13)',
              '& .MuiTooltip-arrow': {
                color: theme.palette.common.white,
                '&::before': {
                  border: `1px solid ${theme.palette.divider}`,
                },
              },
            }
          },
          arrow: {
            sx: {
              color: theme.palette.common.white,
            }
          }
        }}
      >
        <IconButton 
          size={iconSize}
          sx={{ 
            padding: 0,
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
          disableRipple
        >
          <InfoIcon 
            sx={{ 
              fontSize: iconSize === 'small' ? 16 : iconSize === 'medium' ? 20 : 24,
              color: theme.palette.info.main
            }} 
          />
        </IconButton>
      </MuiTooltip>
    </Box>
  );
};

/**
 * Default export for backward compatibility
 */
export default Tooltip; 